import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  resetKey?: string | number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  detailsOpen: boolean;
  copied: boolean;
}

class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, detailsOpen: false, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[AppErrorBoundary] Uncaught render error:', error.message, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorInfo: null, detailsOpen: false, copied: false });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleToggleDetails = () => {
    this.setState(prev => ({ detailsOpen: !prev.detailsOpen }));
  };

  handleCopy = () => {
    const { error, errorInfo } = this.state;
    const text = [
      `Error: ${error?.message ?? 'Unknown error'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `UA: ${navigator.userAgent}`,
      '',
      error?.stack ?? '',
      '',
      'Component stack:',
      errorInfo?.componentStack ?? '',
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, detailsOpen, copied } = this.state;

      return (
        <div className="flex min-h-[60vh] w-full items-center justify-center p-8">
          <div className="max-w-lg rounded-lg border border-red700 bg-red100 px-6 py-5 shadow-sm">
            <p className="mb-2 text-base font-bold text-red700 text-center">Cos poszlo nie tak</p>
            <p className="mb-4 text-sm text-black700 text-center">
              Wystapil nieoczekiwany blad podczas wyswietlania tej strony. Mozesz odswiezyc strone lub przejsc do innej
              sekcji w menu.
            </p>

            <div className="flex justify-center gap-2 mb-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-md bg-red700 px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                Odswiez strone
              </button>
            </div>

            <div className="border-t border-red700 border-opacity-20 pt-3 mt-3">
              <button
                type="button"
                onClick={this.handleToggleDetails}
                className="flex items-center gap-1 text-xs text-black700 hover:text-red700 mx-auto"
              >
                <span>{detailsOpen ? '\u25BC' : '\u25B6'}</span>
                <span>Szczegoly bledu</span>
              </button>

              {detailsOpen && (
                <div className="mt-2">
                  <div className="flex justify-end mb-1">
                    <button
                      type="button"
                      onClick={this.handleCopy}
                      className="flex items-center gap-1 text-xs text-black700 hover:text-red700 border border-gray-300 rounded px-2 py-1"
                    >
                      {copied ? '\u2713 Skopiowano' : '\u2398 Kopiuj do schowka'}
                    </button>
                  </div>
                  <pre className="bg-white rounded p-3 text-xs text-black700 overflow-auto max-h-48 border border-gray-200 whitespace-pre-wrap break-words">
                    {error?.message}
                    {'\n\n'}
                    {error?.stack}
                    {errorInfo?.componentStack && (
                      <>
                        {'\n\nComponent stack:'}
                        {errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
